import { Flex } from '@mantine/core';
import { PlusCircle, Trash } from '@phosphor-icons/react';
import { CustomLabel } from 'components/elements/custom-form-control';
import { useFormState } from 'components/elements/form';
import { SeparatorVertical } from 'components/styles';
import { CardContainer } from 'mantine-components/container/card-container';
import { OneThirdGridContainer } from 'mantine-components/container/grid-container';
import Input from 'mantine-components/elements';
import Button from 'mantine-components/widget/button';
import Text from 'mantine-components/widget/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { StyleObject } from 'styletron-standard';

import { HotelFormType } from './form-type';
import { onAddNewHotelRoom } from '../hooks/use-hotel-default-values';

export default function RoomSection() {
  const { t } = useTranslation();
  const { control } = useFormContext<HotelFormType>();
  const { editable } = useFormState();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'hotelRooms',
    keyName: 'customId',
  });

  const onAdd = () => {
    append(onAddNewHotelRoom());
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  return (
    <CardContainer>
      <Text variant="headingXSmall">{t('cruise:room')}</Text>
      <SeparatorVertical $margin={8} />
      <Flex gap={8} display={{ base: 'none', md: 'flex' }}>
        <div style={{ flex: 1 }}>
          <OneThirdGridContainer>
            <CustomLabel label={t('common:type')} required={editable} />
            <CustomLabel label={t('cruise:room_size')} required={editable} />
            <CustomLabel label={t('common:description')} />
          </OneThirdGridContainer>
        </div>
        {editable && fields.length > 1 && <div style={{ width: 50 }} />}
      </Flex>
      <SeparatorVertical $margin={4} />
      {fields.map((field, idx) => (
        <React.Fragment key={field.customId}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={8}>
            <div style={{ flex: 1 }}>
              <OneThirdGridContainer>
                <div>
                  <CustomLabel
                    label={t('common:type')}
                    required={editable}
                    $style={labelStyle}
                  />
                  <Input
                    type="text"
                    name={`hotelRooms[${idx}].type`}
                    placeholder={t('cruise:room_type')}
                  />
                </div>
                <div>
                  <CustomLabel
                    label={t('cruise:room_size')}
                    required={editable}
                    $style={labelStyle}
                  />
                  <Input
                    type="text"
                    name={`hotelRooms[${idx}].size`}
                    placeholder={t('cruise:room_size')}
                  />
                </div>
                <div>
                  <CustomLabel
                    label={t('common:description')}
                    $style={labelStyle}
                  />
                  <Input
                    type="text"
                    name={`hotelRooms[${idx}].description`}
                    placeholder={t('common:description')}
                  />
                </div>
              </OneThirdGridContainer>
            </div>
            {editable && fields.length > 1 && (
              <Button
                variant="subtle"
                color="red"
                onClick={() => onRemove(idx)}
              >
                <Trash size={20} />
              </Button>
            )}
          </Flex>
          <SeparatorVertical $margin={8} />
        </React.Fragment>
      ))}
      {editable && (
        <Button
          variant="outline"
          onClick={onAdd}
          w="fit-content"
          leftSection={<PlusCircle size={16} />}
        >
          {t('common:add_extra', { extra: t('cruise:room_type') })}
        </Button>
      )}
    </CardContainer>
  );
}

const labelStyle: StyleObject | any = {
  '@media screen and (min-width: 992px)': {
    display: 'none',
  },
};
